/**
 * Real-time Pricing Service
 * Handles automatic price updates, scheduling, and monitoring
 */

import { getWebHotelierPricingBridge } from '../features/webhotelier/services/webHotelierPricingBridge';
import { supabase } from './supabaseClient';
import {
  handleDatabaseOperation,
  type ErrorHandlingResult,
} from '../utils/errorHandling';
import { addDays } from 'date-fns';
import { PRICING_CONFIG } from '../config/pricingConfig';

export interface PricingSchedule {
  id: string;
  propertyId: string;
  name: string;
  active: boolean;
  cronExpression: string;
  lastRun: Date | null;
  nextRun: Date | null;
  status: 'idle' | 'running' | 'completed' | 'failed';
  settings: {
    lookAheadDays: number;
    minConfidence: number;
    maxPriceChange: number;
    dryRun: boolean;
  };
}

export interface PricingExecutionLog {
  id: string;
  scheduleId: string;
  propertyId: string;
  executionTime: Date;
  duration: number;
  status: 'success' | 'failure' | 'partial';
  roomsProcessed: number;
  ratesUpdated: number;
  errors: string[];
  results: unknown;
}

export interface PriceChangeNotification {
  id: string;
  propertyId: string;
  roomTypeId: string;
  date: string;
  oldPrice: number;
  newPrice: number;
  changePercent: number;
  reason: string;
  confidence: number;
  timestamp: Date;
  acknowledged: boolean;
}

export class RealTimePricingService {
  private pricingBridge = getWebHotelierPricingBridge();
  private scheduledJobs = new Map<string, NodeJS.Timeout>();
  private isRunning = new Map<string, boolean>();

  /**
   * Create or update a pricing schedule
   */
  async createSchedule(
    propertyId: string,
    schedule: Omit<PricingSchedule, 'id' | 'lastRun' | 'nextRun' | 'status'>
  ): Promise<ErrorHandlingResult<PricingSchedule>> {
    return handleDatabaseOperation(async () => {
      const nextRun = this.calculateNextRun(schedule.cronExpression);

      const newSchedule: PricingSchedule = {
        id: `schedule-${Date.now()}`,
        ...schedule,
        propertyId,
        lastRun: null,
        nextRun,
        status: 'idle',
      };

      // Save to database
      const { error } = await supabase
        .from('pricing_schedules')
        .upsert({
          id: newSchedule.id,
          property_id: propertyId,
          schedule_name: schedule.name,
          active: schedule.active,
          cron_expression: schedule.cronExpression,
          next_run: nextRun?.toISOString(),
          status: 'idle',
          settings: schedule.settings,
        })
        .select()
        .single();

      if (error) throw error;

      // Start scheduling if active
      if (schedule.active) {
        this.scheduleNextExecution(newSchedule);
      }

      return newSchedule;
    }, `Create pricing schedule for property ${propertyId}`);
  }

  /**
   * Start automatic pricing execution for a property
   */
  async startAutomaticPricing(
    propertyId: string
  ): Promise<ErrorHandlingResult<boolean>> {
    return handleDatabaseOperation(async () => {
      // Get active schedules for property
      const { data: schedules, error } = await supabase
        .from('pricing_schedules')
        .select('*')
        .eq('property_id', propertyId)
        .eq('active', true);

      if (error) throw error;

      let scheduledCount = 0;
      for (const scheduleData of schedules || []) {
        const schedule: PricingSchedule = {
          id: scheduleData.id,
          propertyId: scheduleData.property_id,
          name: scheduleData.schedule_name,
          active: scheduleData.active,
          cronExpression: scheduleData.cron_expression,
          lastRun: scheduleData.last_run
            ? new Date(scheduleData.last_run)
            : null,
          nextRun: scheduleData.next_run
            ? new Date(scheduleData.next_run)
            : null,
          status: scheduleData.status,
          settings: scheduleData.settings || {
            lookAheadDays: PRICING_CONFIG.DEFAULT_LOOK_AHEAD_DAYS,
            minConfidence: PRICING_CONFIG.DEFAULT_MIN_CONFIDENCE,
            maxPriceChange: PRICING_CONFIG.DEFAULT_MAX_PRICE_CHANGE,
            dryRun: false,
          },
        };

        this.scheduleNextExecution(schedule);
        scheduledCount++;
      }

      console.log(
        `Started ${scheduledCount} automatic pricing schedules for property ${propertyId}`
      );
      return true;
    }, `Start automatic pricing for property ${propertyId}`);
  }

  /**
   * Stop automatic pricing for a property
   */
  stopAutomaticPricing(propertyId: string): void {
    // Clear scheduled jobs for this property
    for (const [scheduleId, timeout] of this.scheduledJobs) {
      if (scheduleId.includes(propertyId)) {
        clearTimeout(timeout);
        this.scheduledJobs.delete(scheduleId);
      }
    }

    // Mark as not running
    this.isRunning.delete(propertyId);

    console.log(`Stopped automatic pricing for property ${propertyId}`);
  }

  /**
   * Execute pricing strategy manually
   */
  async executeNow(
    propertyId: string,
    options: {
      lookAheadDays?: number;
      minConfidence?: number;
      maxPriceChange?: number;
      dryRun?: boolean;
    } = {}
  ): Promise<ErrorHandlingResult<PricingExecutionLog>> {
    return handleDatabaseOperation(async () => {
      const startTime = Date.now();

      if (this.isRunning.get(propertyId)) {
        throw new Error('Pricing execution already running for this property');
      }

      this.isRunning.set(propertyId, true);

      try {
        const {
          lookAheadDays = PRICING_CONFIG.DEFAULT_LOOK_AHEAD_DAYS,
          minConfidence = PRICING_CONFIG.DEFAULT_MIN_CONFIDENCE,
          maxPriceChange = PRICING_CONFIG.DEFAULT_MAX_PRICE_CHANGE,
          dryRun = false,
        } = options;

        // Calculate date range
        const startDate = new Date();
        const endDate = addDays(startDate, lookAheadDays);

        // Execute pricing strategy
        const executionResult = await this.pricingBridge.executePricingStrategy(
          propertyId,
          startDate,
          endDate,
          { dryRun, minConfidence, maxPriceChange }
        );

        const duration = Date.now() - startTime;

        const log: PricingExecutionLog = {
          id: `log-${Date.now()}`,
          scheduleId: 'manual',
          propertyId,
          executionTime: new Date(),
          duration,
          status: executionResult.success ? 'success' : 'failure',
          roomsProcessed: executionResult.success
            ? executionResult.data!.processedRooms
            : 0,
          ratesUpdated: executionResult.success
            ? executionResult.data!.updatedRates
            : 0,
          errors: executionResult.success
            ? executionResult.data!.errors.map((e) => e.error)
            : [executionResult.error?.message || 'Unknown error'],
          results: executionResult.data,
        };

        // Save execution log
        await this.saveExecutionLog(log);

        // Generate notifications for significant price changes
        if (
          executionResult.success &&
          executionResult.data!.priceChanges.length > 0
        ) {
          await this.generatePriceChangeNotifications(
            propertyId,
            executionResult.data!.priceChanges
          );
        }

        return log;
      } finally {
        this.isRunning.set(propertyId, false);
      }
    }, `Execute pricing strategy for property ${propertyId}`);
  }

  /**
   * Schedule next execution for a pricing schedule
   */
  private scheduleNextExecution(schedule: PricingSchedule): void {
    if (!schedule.active || !schedule.nextRun) return;

    const now = new Date();
    const delay = schedule.nextRun.getTime() - now.getTime();

    if (delay <= 0) {
      // Should run now or overdue
      this.executeScheduledPricing(schedule);
      return;
    }

    // Schedule for future execution
    const timeout = setTimeout(() => {
      this.executeScheduledPricing(schedule);
    }, delay);

    this.scheduledJobs.set(schedule.id, timeout);

    console.log(
      `Scheduled pricing execution for ${schedule.name} at ${schedule.nextRun.toLocaleString()}`
    );
  }

  /**
   * Execute scheduled pricing
   */
  private async executeScheduledPricing(
    schedule: PricingSchedule
  ): Promise<void> {
    if (this.isRunning.get(schedule.propertyId)) {
      console.log(
        `Skipping scheduled execution - already running for property ${schedule.propertyId}`
      );
      return;
    }

    console.log(`Executing scheduled pricing: ${schedule.name}`);

    try {
      // Update schedule status
      await this.updateScheduleStatus(schedule.id, 'running');

      // Execute pricing
      const result = await this.executeNow(
        schedule.propertyId,
        schedule.settings
      );

      if (result.success) {
        await this.updateScheduleStatus(schedule.id, 'completed');
      } else {
        await this.updateScheduleStatus(schedule.id, 'failed');
      }

      // Calculate and schedule next run
      const nextRun = this.calculateNextRun(schedule.cronExpression);
      if (nextRun) {
        await this.updateScheduleNextRun(schedule.id, nextRun);
        schedule.nextRun = nextRun;
        this.scheduleNextExecution(schedule);
      }
    } catch (error) {
      console.error('Scheduled pricing execution failed:', error);
      await this.updateScheduleStatus(schedule.id, 'failed');
    }
  }

  /**
   * Calculate next run time based on cron expression
   */
  private calculateNextRun(cronExpression: string): Date | null {
    // Simple cron parser for common patterns
    // Format: "minute hour day month dayOfWeek"

    const now = new Date();

    // Handle common patterns
    if (cronExpression === '0 3 * * *') {
      // Daily at 3 AM
      const nextRun = new Date(now);
      nextRun.setHours(3, 0, 0, 0);
      if (nextRun <= now) {
        nextRun.setDate(nextRun.getDate() + 1);
      }
      return nextRun;
    }

    if (cronExpression === '0 */6 * * *') {
      // Every 6 hours
      const nextRun = new Date(now);
      const currentHour = nextRun.getHours();
      const nextHour = Math.ceil((currentHour + 1) / 6) * 6;
      nextRun.setHours(nextHour, 0, 0, 0);
      if (nextRun <= now) {
        nextRun.setHours(nextRun.getHours() + 6);
      }
      return nextRun;
    }

    if (cronExpression === '0 */1 * * *') {
      // Every hour
      const nextRun = new Date(now);
      nextRun.setMinutes(0, 0, 0);
      nextRun.setHours(nextRun.getHours() + 1);
      return nextRun;
    }

    // Default: 24 hours from now
    return addDays(now, 1);
  }

  /**
   * Update schedule status in database
   */
  private async updateScheduleStatus(
    scheduleId: string,
    status: string
  ): Promise<void> {
    const updates: { status: string; last_run?: string } = { status };

    if (status === 'running') {
      updates.last_run = new Date().toISOString();
    }

    await supabase
      .from('pricing_schedules')
      .update(updates)
      .eq('id', scheduleId);
  }

  /**
   * Update schedule next run time
   */
  private async updateScheduleNextRun(
    scheduleId: string,
    nextRun: Date
  ): Promise<void> {
    await supabase
      .from('pricing_schedules')
      .update({ next_run: nextRun.toISOString() })
      .eq('id', scheduleId);
  }

  /**
   * Save execution log to database
   */
  private async saveExecutionLog(log: PricingExecutionLog): Promise<void> {
    await supabase.from('pricing_execution_logs').insert({
      id: log.id,
      schedule_id: log.scheduleId,
      property_id: log.propertyId,
      execution_time: log.executionTime.toISOString(),
      duration: log.duration,
      status: log.status,
      rooms_processed: log.roomsProcessed,
      rates_updated: log.ratesUpdated,
      errors: log.errors,
      results: log.results,
    });
  }

  /**
   * Generate price change notifications
   */
  private async generatePriceChangeNotifications(
    propertyId: string,
    priceChanges: Array<{
      roomType: string;
      date: string;
      oldPrice: number;
      newPrice: number;
      adjustment: number;
    }>
  ): Promise<void> {
    const notifications: Omit<PriceChangeNotification, 'id'>[] = [];

    for (const change of priceChanges) {
      const changePercent = Math.abs(change.adjustment / change.oldPrice) * 100;

      // Only notify for significant changes (>10% or >€20)
      if (changePercent > 10 || Math.abs(change.adjustment) > 20) {
        notifications.push({
          propertyId,
          roomTypeId: change.roomType,
          date: change.date,
          oldPrice: change.oldPrice,
          newPrice: change.newPrice,
          changePercent,
          reason: `Automatic pricing adjustment: ${change.adjustment > 0 ? '+' : ''}€${change.adjustment.toFixed(2)}`,
          confidence: 0.8, // Default confidence
          timestamp: new Date(),
          acknowledged: false,
        });
      }
    }

    if (notifications.length > 0) {
      await supabase.from('price_change_notifications').insert(
        notifications.map((n) => ({
          property_id: n.propertyId,
          room_type_id: n.roomTypeId,
          date: n.date,
          old_price: n.oldPrice,
          new_price: n.newPrice,
          change_percent: n.changePercent,
          reason: n.reason,
          confidence: n.confidence,
          timestamp: n.timestamp.toISOString(),
          acknowledged: n.acknowledged,
        }))
      );

      console.log(
        `Generated ${notifications.length} price change notifications for property ${propertyId}`
      );
    }
  }

  /**
   * Get pricing execution history
   */
  async getExecutionHistory(
    propertyId: string,
    limit: number = 50
  ): Promise<ErrorHandlingResult<PricingExecutionLog[]>> {
    return handleDatabaseOperation(async () => {
      const { data, error } = await supabase
        .from('pricing_execution_logs')
        .select('*')
        .eq('property_id', propertyId)
        .order('execution_time', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((log) => ({
        id: log.id,
        scheduleId: log.schedule_id,
        propertyId: log.property_id,
        executionTime: new Date(log.execution_time),
        duration: log.duration,
        status: log.status,
        roomsProcessed: log.rooms_processed,
        ratesUpdated: log.rates_updated,
        errors: log.errors || [],
        results: log.results,
      }));
    }, `Get execution history for property ${propertyId}`);
  }

  /**
   * Get unacknowledged price change notifications
   */
  async getUnacknowledgedNotifications(
    propertyId: string
  ): Promise<ErrorHandlingResult<PriceChangeNotification[]>> {
    return handleDatabaseOperation(async () => {
      const { data, error } = await supabase
        .from('price_change_notifications')
        .select('*')
        .eq('property_id', propertyId)
        .eq('acknowledged', false)
        .order('timestamp', { ascending: false });

      if (error) throw error;

      return (data || []).map((notification) => ({
        id: notification.id,
        propertyId: notification.property_id,
        roomTypeId: notification.room_type_id,
        date: notification.date,
        oldPrice: notification.old_price,
        newPrice: notification.new_price,
        changePercent: notification.change_percent,
        reason: notification.reason,
        confidence: notification.confidence,
        timestamp: new Date(notification.timestamp),
        acknowledged: notification.acknowledged,
      }));
    }, `Get notifications for property ${propertyId}`);
  }

  /**
   * Acknowledge price change notification
   */
  async acknowledgeNotification(
    notificationId: string
  ): Promise<ErrorHandlingResult<boolean>> {
    return handleDatabaseOperation(async () => {
      const { error } = await supabase
        .from('price_change_notifications')
        .update({ acknowledged: true })
        .eq('id', notificationId);

      if (error) throw error;
      return true;
    }, `Acknowledge notification ${notificationId}`);
  }

  /**
   * Get current execution status for a property
   */
  isExecuting(propertyId: string): boolean {
    return this.isRunning.get(propertyId) || false;
  }

  /**
   * Get active schedules for a property
   */
  async getActiveSchedules(
    propertyId: string
  ): Promise<ErrorHandlingResult<PricingSchedule[]>> {
    return handleDatabaseOperation(async () => {
      const { data, error } = await supabase
        .from('pricing_schedules')
        .select('*')
        .eq('property_id', propertyId)
        .eq('active', true);

      if (error) throw error;

      return (data || []).map((schedule) => ({
        id: schedule.id,
        propertyId: schedule.property_id,
        name: schedule.schedule_name,
        active: schedule.active,
        cronExpression: schedule.cron_expression,
        lastRun: schedule.last_run ? new Date(schedule.last_run) : null,
        nextRun: schedule.next_run ? new Date(schedule.next_run) : null,
        status: schedule.status,
        settings: schedule.settings || {
          lookAheadDays: PRICING_CONFIG.DEFAULT_LOOK_AHEAD_DAYS,
          minConfidence: PRICING_CONFIG.DEFAULT_MIN_CONFIDENCE,
          maxPriceChange: PRICING_CONFIG.DEFAULT_MAX_PRICE_CHANGE,
          dryRun: false,
        },
      }));
    }, `Get active schedules for property ${propertyId}`);
  }
}

// Export singleton
let realTimePricingService: RealTimePricingService | null = null;

export function getRealTimePricingService(): RealTimePricingService {
  try {
    console.log('[Real-Time Pricing Service] Initializing service...');

    if (!realTimePricingService) {
      console.log('[Real-Time Pricing Service] Creating new instance');
      realTimePricingService = new RealTimePricingService();
      console.log('[Real-Time Pricing Service] Instance created successfully');
    }

    return realTimePricingService;
  } catch (error) {
    console.error(
      '[Real-Time Pricing Service] Error creating instance:',
      error
    );

    // Create a safe fallback instance that won't crash
    if (!realTimePricingService) {
      try {
        realTimePricingService = new RealTimePricingService();
      } catch (fallbackError) {
        console.error(
          '[Real-Time Pricing Service] Fallback creation also failed:',
          fallbackError
        );
        throw new Error('Failed to create Real-Time Pricing Service instance');
      }
    }

    return realTimePricingService;
  }
}

export default RealTimePricingService;
